import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Table,
  TableBody,
  Button,
  TextField,
  InputAdornment,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TableSortLabel,
  Container,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Loading } from "../../components/Loading";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";

import { financeApi } from "./apiService";
import { usePlanStatus } from "../../hooks/usePlanStatus";
import {
  commonTextFieldProps,
  smallButtonProp,
  tableHeaderStyles,
  tableRowStyles,
} from "../../utils/styles";
import { formatCurrency, formatText } from "../../utils/utils";
import { Add, Close, Info, Search } from "@mui/icons-material";
import BusinessIcon from "@mui/icons-material/Business";
import PeopleIcon from "@mui/icons-material/People";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import ComingSoon from "../../components/ComingSoon";
import CustomChip from "../../components/CustomChip";
import {
  incomePartyTypeConfig,
  getFilteredPartyCategories,
} from "./components/PartySummaryCards";
import React from "react";
import NavigationHeader from "../../components/NavigationHeader";
import IncomePaymentModal from "./components/IncomePaymentModal";

export const tooltipInfo = {
  totalBooking: "Total of amount of bookings made with the party.",
  netReceivable: "Expected amount to be received from the party.",
  netReceived:
    "Amount received from the party, this includes amount from settlements as well.",
  totalBalance: "Total balance of the party.",
};

const IncomePartyDetails = () => {
  const [isLoading, setIsLoading] = useState(true);
  const { planStatus } = usePlanStatus();

  // Get filtered party categories based on operation type
  const partyCategories = getFilteredPartyCategories(
    "income",
    planStatus?.operationType
  );
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { partyType } = useParams<{ partyType: string }>();
  const [partyListWithBalance, setPartyListWithBalance] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState("");
  // Transaction Modal State
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedParty, setSelectedParty] = useState<any>(null);
  const [selectedPartyType, setSelectedPartyType] = useState<string>("");

  // Sorting and filtering state
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  // Enhanced filtering and sorting logic
  const getFilteredAndSortedParties = () => {
    let filtered = (partyListWithBalance || []).filter((party: any) => {
      // Search filter
      if (searchQuery.trim()) {
        const searchTerm = searchQuery.toLowerCase().trim();
        const partyName = party?.name?.toLowerCase() || "";
        const partyPhone = party?.phoneNumber?.toLowerCase() || "";
        const partyTypeName = party?.type?.toLowerCase() || "";

        const matchesSearch =
          partyName.includes(searchTerm) ||
          partyPhone.includes(searchTerm) ||
          partyTypeName.includes(searchTerm);
        if (!matchesSearch) return false;
      }

      return true;
    });

    // Sorting
    if (sortBy) {
      filtered.sort((a: any, b: any) => {
        let aValue: any;
        let bValue: any;

        switch (sortBy) {
          case "name":
            aValue = a?.name?.toLowerCase() || "";
            bValue = b?.name?.toLowerCase() || "";
            break;
          case "transactions":
            aValue = a?.totalTransactions || 0;
            bValue = b?.totalTransactions || 0;
            break;
          case "booking":
            aValue = a?.totalBooking || 0;
            bValue = b?.totalBooking || 0;
            break;
          case "netReceivable":
            aValue = a?.netReceivable || 0;
            bValue = b?.netReceivable || 0;
            break;
          case "netReceived":
            aValue = a?.netReceived || 0;
            bValue = b?.netReceived || 0;
            break;
          case "balance":
            aValue = a?.totalBalance || 0;
            bValue = b?.totalBalance || 0;
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
        if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  };

  const filteredParties = getFilteredAndSortedParties();

  // Helper function to get user-friendly sort label
  const getSortLabel = (sortKey: string) => {
    switch (sortKey) {
      case "name":
        return "Name";
      case "transactions":
        return "Transactions";
      case "booking":
        return "Booking";
      case "netReceivable":
        return "Receivable";
      case "netReceived":
        return "Received";
      case "balance":
        return "Balance";
      default:
        return sortKey;
    }
  };

  // Sorting and filtering handlers
  const handleSortChange = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSortBy("");
    setSortOrder("asc");
  };

  const getPartyListWithBalance = async (partyTypeParam: string) => {
    try {
      setIsLoading(true);
      const response = await financeApi.getPartyListWithBalance(
        partyTypeParam,
        "income"
      );

      // Extract party list and summary from response
      if (response && typeof response === "object") {
        // If response has summary and partyList structure (new API format)
        if (response.summary && response.partyList) {
          setPartyListWithBalance(response.partyList);
          setSummary(response.summary);
        }
        // If response has summary and parties structure
        else if (response.summary && response.parties) {
          setPartyListWithBalance(response.parties);
          setSummary(response.summary);
        }
        // If response has summary property directly
        else if (response.summary) {
          setPartyListWithBalance(response.partyList || response.parties || []);
          setSummary(response.summary);
        }
        // Fallback: treat as array of parties (existing behavior)
        else {
          setPartyListWithBalance(Array.isArray(response) ? response : []);
          setSummary(null);
        }
      } else {
        setPartyListWithBalance(response || []);
        setSummary(null);
      }
    } catch (error) {
      console.error("Error fetching party list with balance:", error);
      setPartyListWithBalance([]);
      setSummary(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (planStatus?.company?.id && partyType) {
      getPartyListWithBalance(partyType);
      // Update selectedPartyType when URL partyType changes
      setSelectedPartyType(partyType);
    } else {
      setIsLoading(false);
    }
  }, [partyType, planStatus]);

  // Initialize state from URL params on mount
  useEffect(() => {
    // Initialize selectedPartyType from URL param
    if (partyType) {
      setSelectedPartyType(partyType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Transaction Functions
  const handleShowTransactions = (party: any) => {
    setSelectedParty(party);
    setPaymentModalOpen(true);
  };

  const handleCloseModal = () => {
    setPaymentModalOpen(false);
    setSelectedParty(null);
  };

  const handleTransactionAdded = async () => {
    // Refresh party list when a transaction is added
    if (partyType) {
      await getPartyListWithBalance(partyType);
    }
  };

  // Handle party type dropdown change
  const handlePartyChange = (event: any) => {
    const newPartyType = event.target.value;
    setSelectedPartyType(newPartyType);

    // Navigate to the new party type URL
    if (newPartyType) {
      navigate(`/finance/income/party/${newPartyType}`);
    }
  };

  if (isLoading && !partyType) {
    return <Loading message="Loading financial data..." fullScreen={true} />;
  }

  const currentCategory = partyCategories.find(
    (cat) => cat.value === partyType
  );
  const categoryTitle = currentCategory?.title || formatText(partyType || "");

  // Get party config for icon and styling
  const config = partyType
    ? incomePartyTypeConfig[partyType as keyof typeof incomePartyTypeConfig]
    : null;

  const renderContent = () => {
    if (!partyType) {
      return null;
    }
    if (isLoading) {
      return (
        <Grid container spacing={2}>
          {/* Card skeleton */}
          <Grid item xs={12}>
            <Card sx={{ height: "100%" }}>
              <CardContent sx={{ p: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                  Loading...
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }
    return (
      <Grid container spacing={1}>
        {/* Summary Card */}
        {config && (
          <Grid item xs={12}>
            <Card>
              <CardContent
                sx={{ p: 0.5, py: 0.5, "&:last-child": { pb: 0.5 } }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 0.5,
                  }}
                >
                  {/* Icon and Title */}
                  <Box
                    sx={{
                      backgroundColor: "primary.main",
                      color: "white",
                      borderRadius: "50%",
                      width: 28,
                      height: 28,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {React.cloneElement(config.icon, { sx: { fontSize: 16 } })}
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{ minWidth: 100, fontSize: "0.9rem" }}
                  >
                    {config.title}
                  </Typography>

                  {/* All Stats in one row */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flex: 1,
                      justifyContent: "space-around",
                    }}
                  >
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {summary?.totalParties}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.6rem" }}
                      >
                        Parties
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "center" }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{ fontSize: "0.8rem" }}
                      >
                        {summary?.totalTransactions}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: "0.6rem" }}
                      >
                        Transactions
                      </Typography>
                    </Box>
                    <Tooltip title={tooltipInfo.totalBooking} placement="top">
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {formatCurrency(summary?.totalBooking)}
                        </Typography>
                        <>
                          <Info
                            sx={{ fontSize: "0.7rem", mr: 0.5 }}
                            color="primary"
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            Booking
                          </Typography>
                        </>
                      </Box>
                    </Tooltip>
                    <Tooltip title={tooltipInfo.netReceivable} placement="top">
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {formatCurrency(summary?.netReceivable)}
                        </Typography>
                        <>
                          <Info
                            sx={{ fontSize: "0.7rem", mr: 0.5 }}
                            color="primary"
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            Receivable
                          </Typography>
                        </>
                      </Box>
                    </Tooltip>
                    <Tooltip title={tooltipInfo.netReceived} placement="top">
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {formatCurrency(summary?.netReceived)}
                        </Typography>
                        <>
                          <Info
                            sx={{ fontSize: "0.7rem", mr: 0.5 }}
                            color="primary"
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            Received
                          </Typography>
                        </>
                      </Box>
                    </Tooltip>
                    <Tooltip title={tooltipInfo.totalBalance} placement="top">
                      <Box sx={{ textAlign: "center" }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          sx={{ fontSize: "0.8rem" }}
                        >
                          {formatCurrency(summary?.totalBalance)}
                        </Typography>
                        <>
                          <Info
                            sx={{ fontSize: "0.7rem", mr: 0.5 }}
                            color="primary"
                          />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontSize: "0.7rem" }}
                          >
                            Balance
                          </Typography>
                        </>
                      </Box>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
        {/* Recent Transactions */}
        <Grid item xs={12}>
          <Card sx={{ height: "100%" }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ mb: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                    Parties
                  </Typography>
                  {/* Search Bar */}
                  <Box
                    sx={{
                      mb: 2,
                      width: "30%",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <TextField
                      fullWidth
                      label="Search by name, phone, or type"
                      placeholder="Enter party name, phone number, or type..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      {...commonTextFieldProps}
                      InputProps={{
                        endAdornment: searchQuery.length > 0 && (
                          <InputAdornment position="end">
                            <Close
                              style={{ fontSize: "1rem", cursor: "pointer" }}
                              onClick={() => setSearchQuery("")}
                            />
                          </InputAdornment>
                        ),
                        startAdornment: (
                          <Search
                            style={{ fontSize: "1rem", marginRight: 2 }}
                          />
                        ),
                      }}
                    />
                    {searchQuery && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          display: "block",
                          fontSize: "0.7rem",
                          textAlign: "right",
                        }}
                      >
                        {filteredParties.length} of{" "}
                        {partyListWithBalance.length} parties found
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Active Filters Display */}
                <Box
                  sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}
                >
                  {(searchQuery || sortBy) && (
                    <>
                      <Typography variant="caption" color="text.secondary">
                        Filters:
                      </Typography>
                      {searchQuery && (
                        <Chip
                          label={`Search: ${searchQuery}`}
                          size="small"
                          onDelete={() => setSearchQuery("")}
                          variant="outlined"
                        />
                      )}
                      {sortBy && (
                        <Chip
                          label={`Sort: ${getSortLabel(
                            sortBy
                          )} ${sortOrder.toUpperCase()}`}
                          size="small"
                          onDelete={() => {
                            setSortBy("");
                            setSortOrder("asc");
                          }}
                          variant="outlined"
                        />
                      )}
                      <Button
                        size="small"
                        onClick={clearFilters}
                        sx={{ fontSize: "0.7rem" }}
                      >
                        Clear All
                      </Button>
                    </>
                  )}
                </Box>
              </Box>

              <TableContainer>
                <Table
                  size="small"
                  aria-label="party list table"
                  sx={{
                    tableLayout: "auto",
                    "& .MuiTableCell-root": {
                      padding: "4px 8px",
                      fontSize: "0.75rem",
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeaderStyles}>
                        <TableSortLabel
                          active={sortBy === "name"}
                          direction={sortBy === "name" ? sortOrder : "asc"}
                          onClick={() => handleSortChange("name")}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={tableHeaderStyles}>Phone</TableCell>
                      <TableCell sx={tableHeaderStyles}>
                        <TableSortLabel
                          active={sortBy === "transactions"}
                          direction={
                            sortBy === "transactions" ? sortOrder : "asc"
                          }
                          onClick={() => handleSortChange("transactions")}
                        >
                          Transactions
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={tableHeaderStyles}>
                        <TableSortLabel
                          active={sortBy === "booking"}
                          direction={sortBy === "booking" ? sortOrder : "asc"}
                          onClick={() => handleSortChange("booking")}
                        >
                          Booking
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={tableHeaderStyles}>
                        <TableSortLabel
                          active={sortBy === "netReceivable"}
                          direction={
                            sortBy === "netReceivable" ? sortOrder : "asc"
                          }
                          onClick={() => handleSortChange("netReceivable")}
                        >
                          Receivable
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={tableHeaderStyles}>
                        <TableSortLabel
                          active={sortBy === "netReceived"}
                          direction={
                            sortBy === "netReceived" ? sortOrder : "asc"
                          }
                          onClick={() => handleSortChange("netReceived")}
                        >
                          Received
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={tableHeaderStyles}>
                        <TableSortLabel
                          active={sortBy === "balance"}
                          direction={sortBy === "balance" ? sortOrder : "asc"}
                          onClick={() => handleSortChange("balance")}
                        >
                          Balance
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={tableHeaderStyles}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredParties.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={8}
                          sx={{
                            textAlign: "center",
                            py: 4,
                            fontSize: "0.75rem",
                          }}
                        >
                          {searchQuery ? (
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mb: 1 }}
                              >
                                No parties found matching "{searchQuery}"
                              </Typography>
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => setSearchQuery("")}
                                sx={{ fontSize: "0.7rem" }}
                              >
                                Clear Search
                              </Button>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No parties available for this category
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredParties.map((party) => (
                        <TableRow key={party?.id}>
                          <TableCell sx={tableRowStyles}>
                            {party?.name}
                          </TableCell>
                          <TableCell sx={tableRowStyles}>
                            {party?.phoneNumber || "-"}
                          </TableCell>
                          <TableCell sx={tableRowStyles}>
                            {party?.totalTransactions || 0}
                          </TableCell>
                          <TableCell sx={tableRowStyles}>
                            {formatCurrency(party?.totalBooking || 0)}
                          </TableCell>
                          <TableCell sx={tableRowStyles}>
                            {formatCurrency(party?.netReceivable || 0)}
                          </TableCell>
                          <TableCell sx={tableRowStyles}>
                            {formatCurrency(party?.netReceived || 0)}
                          </TableCell>
                          <TableCell sx={tableRowStyles}>
                            {formatCurrency(party?.totalBalance || 0)}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button
                                variant="outlined"
                                color="primary"
                                sx={smallButtonProp.sx}
                                startIcon={<Add style={{ fontSize: "1rem" }} />}
                                onClick={() => handleShowTransactions(party)}
                              >
                                Payment
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                sx={smallButtonProp.sx}
                                onClick={() =>
                                  navigate(
                                    `/finance/transactions/income/${party?.id}`,
                                    {
                                      state: {
                                        party: party,
                                        partyType: partyType,
                                      },
                                    }
                                  )
                                }
                              >
                                Show Transactions
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <NavigationHeader
        title={
          partyType
            ? `${categoryTitle} - Receivables`
            : "Receivables - Party Details"
        }
        backNavigationEnabled={true}
        backNavigationTo="/finance/income"
        breadcrumbs={[
          { label: "Finance", path: "/finance" },
          { label: "Receivables", path: "/finance/income" },
          { label: partyType ? categoryTitle : "Party Details" },
        ]}
      />

      {/* Party Selection Dropdown */}
      <Box sx={{ mb: 2 }}>
        <FormControl
          fullWidth
          size="small"
          sx={{ maxWidth: 400, fontSize: "0.75rem" }}
        >
          <InputLabel id="party-select-label" sx={{ fontSize: "0.75rem" }}>
            Select Party
          </InputLabel>
          <Select
            labelId="party-select-label"
            id="party-select"
            value={selectedPartyType || ""}
            label="Select Party"
            onChange={handlePartyChange}
            {...commonTextFieldProps.SelectProps}
          >
            {partyCategories.map((category) => (
              <MenuItem key={category.id} value={category.value}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography sx={{ fontSize: "0.75rem" }}>
                    {category.title}
                  </Typography>
                  <CustomChip value={category.financialType} />
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Show message when no party type is selected */}
      {!partyType && (
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              Select a Party Type
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please select a party type from the dropdown above to view
              receivables details.
            </Typography>
          </CardContent>
        </Card>
      )}

      {renderContent()}

      {/* Transaction Modal */}
      <IncomePaymentModal
        open={paymentModalOpen}
        onClose={handleCloseModal}
        selectedParty={selectedParty}
        onTransactionAdded={handleTransactionAdded}
        partyType={partyType || ""}
      />
    </Container>
  );
};

export default IncomePartyDetails;
